"use client";

import * as React from "react";
import { signOut } from "next-auth/react";
import { toast } from "sonner";
import {
  ArrowRight,
  CreditCard,
  History,
  LogOut,
  Plus,
  ShieldCheck,
  ShoppingCart,
  Wifi,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { faNum } from "@/lib/format";
import { useAppStore } from "@/stores/useAppStore";

import { BuyPlanFlow, type Plan } from "./BuyPlanFlow";
import { PaymentHistory, type Payment } from "./PaymentHistory";
import { PlanPicker } from "./PlanPicker";
import { VpnConfigCard, type Subscription } from "./VpnConfigCard";

type SessionUser = {
  id: string;
  name: string;
  phone: string;
  role: string;
};

type DashData = {
  user: SessionUser | null;
  subscriptions: Subscription[];
  payments: Payment[];
  plans: Plan[];
};

export default function UserDashboard({
  initialBuyPlanId,
}: {
  initialBuyPlanId?: string;
}) {
  const [data, setData] = React.useState<DashData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [buyPlan, setBuyPlan] = React.useState<Plan | null>(null);

  const buyPlanId = useAppStore((s) => s.buyPlanId);
  const resetBuy = useAppStore((s) => s.resetBuy);

  const plansRef = React.useRef<HTMLDivElement | null>(null);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [sessionRes, subsRes, paysRes, plansRes] = await Promise.all([
        fetch("/api/session", { cache: "no-store" }),
        fetch("/api/subscriptions", { cache: "no-store" }),
        fetch("/api/payments", { cache: "no-store" }),
        fetch("/api/plans", { cache: "no-store" }),
      ]);
      const [session, subs, pays, plans] = await Promise.all([
        sessionRes.json(),
        subsRes.json(),
        paysRes.json(),
        plansRes.json(),
      ]);
      setData({
        user: session.user || null,
        subscriptions: subs.subscriptions ?? [],
        payments: pays.payments ?? [],
        plans: plans.plans ?? [],
      });
    } catch {
      setError(true);
      toast.error("بارگذاری داشبورد ناموفق بود");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Auto-open BuyPlanFlow when buyPlanId set (from store or initial prop).
  React.useEffect(() => {
    const id = buyPlanId || initialBuyPlanId;
    if (!id || !data) return;
    const plan = data.plans.find((p) => p.id === id);
    if (plan) {
      setBuyPlan(plan);
      resetBuy();
    }
  }, [buyPlanId, initialBuyPlanId, data, resetBuy]);

  const onBuyClick = (planId: string) => {
    const plan = data?.plans.find((p) => p.id === planId);
    if (plan) setBuyPlan(plan);
    else toast.error("طرح یافت نشد");
  };

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const user = data?.user;
  const activeSubs =
    data?.subscriptions.filter((s) => s.status === "ACTIVE") ?? [];
  const hasActive = activeSubs.length > 0;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => pushView("dashboard")}
            className="flex items-center gap-2 text-right"
          >
            <ArrowRight className="size-4" />
            <div className="leading-tight">
              <p className="text-sm font-bold">فَران</p>
              <p className="text-[10px] text-muted-foreground">پنل کاربری</p>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <span className="flex size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <ShieldCheck className="size-5" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-bold">فَران</p>
              <p className="text-[10px] text-muted-foreground">داشبورد شبکه</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {user && (
              <div className="hidden text-left sm:block">
                <p className="text-sm font-medium leading-tight">{user.name}</p>
                <p dir="ltr" className="text-[11px] text-muted-foreground">
                  {user.phone}
                </p>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => signOut({ callbackUrl: "/" })}
            >
              <LogOut className="size-4" /> خروج
            </Button>
            <Button size="sm" className="gap-1.5" onClick={scrollToPlans}>
              <Plus className="size-4" /> خرید اشتراک جدید
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-6">
          {error && (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              بارگذاری داشبورد ناموفق بود. لطفاً صفحه را بازخوانی کنید.
            </div>
          )}

          {/* Section 1: Active subscriptions */}
          <section aria-labelledby="subs-heading">
            <div className="mb-4 flex items-center gap-2">
              <Wifi className="size-5 text-primary" />
              <h2 id="subs-heading" className="text-lg font-bold">
                اشتراک‌های فعال
              </h2>
              {hasActive && (
                <Badge className="border-primary/30 bg-primary/15 text-primary">
                  {faNum(activeSubs.length)}
                </Badge>
              )}
            </div>

            {loading ? (
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <Skeleton className="h-72 rounded-2xl" />
                <Skeleton className="h-72 rounded-2xl" />
              </div>
            ) : hasActive ? (
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {activeSubs.map((sub) => (
                  <VpnConfigCard key={sub.id} subscription={sub} />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed bg-card p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  هنوز اشتراکی ندارید
                </p>
                <Button className="mt-3 gap-2" onClick={scrollToPlans}>
                  <ShoppingCart className="size-4" /> خرید اشتراک
                </Button>
              </div>
            )}
          </section>

          {/* Section 2: Plans */}
          <section ref={plansRef} aria-labelledby="plans-heading">
            <div className="mb-4 flex items-center gap-2">
              <CreditCard className="size-5 text-primary" />
              <h2 id="plans-heading" className="text-lg font-bold">
                انتخاب طرح
              </h2>
            </div>
            {loading ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-72 rounded-2xl" />
                ))}
              </div>
            ) : (
              <PlanPicker plans={data?.plans ?? []} onBuy={onBuyClick} />
            )}
          </section>

          {/* Section 3: Payment history */}
          <section aria-labelledby="pays-heading">
            <div className="mb-4 flex items-center gap-2">
              <History className="size-5 text-primary" />
              <h2 id="pays-heading" className="text-lg font-bold">
                تاریخچه پرداخت
              </h2>
            </div>
            {loading ? (
              <Skeleton className="h-48 rounded-2xl" />
            ) : (
              <PaymentHistory payments={data?.payments ?? []} />
            )}
          </section>
        </div>
      </main>

      <footer className="mt-auto border-t bg-card/40 py-5">
        <div className="mx-auto w-full max-w-6xl px-4 text-center text-xs text-muted-foreground">
          © {faNum(new Date().getFullYear())} آکادمی فَران — تمام حقوق محفوظ است.
        </div>
      </footer>

      {buyPlan && (
        <BuyPlanFlow
          plan={buyPlan}
          onClose={() => setBuyPlan(null)}
          onSubmitted={() => {
            setBuyPlan(null);
            loadAll();
          }}
        />
      )}
    </div>
  );
}
